// Coordination of summary runs (see jobManager.ts): a semaphore caps how many summaries run at
// the same time.

function abortError(signal: AbortSignal | undefined): unknown {
  return signal?.reason ?? new DOMException('Aborted', 'AbortError');
}

export interface Semaphore {
  /**
   * Resolves with a release function once a slot is free. Calls `onWait` first when it has
   * to wait. Rejects when `signal` aborts while waiting.
   */
  acquire(signal?: AbortSignal, onWait?: () => void): Promise<() => void>;
  readonly active: number;
  readonly waiting: number;
}

/** FIFO semaphore with abortable waiting. */
export function createSemaphore(limit: number): Semaphore {
  let active = 0;
  const queue: Array<() => void> = [];

  const release = () => {
    active--;
    queue.shift()?.();
  };
  const grant = () => {
    active++;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      release();
    };
  };

  return {
    get active() {
      return active;
    },
    get waiting() {
      return queue.length;
    },
    acquire(signal, onWait) {
      if (signal?.aborted) return Promise.reject(abortError(signal));
      if (active < limit && queue.length === 0) return Promise.resolve(grant());
      onWait?.();
      return new Promise((resolve, reject) => {
        const entry = () => {
          signal?.removeEventListener('abort', onAbort);
          resolve(grant());
        };
        const onAbort = () => {
          const index = queue.indexOf(entry);
          if (index !== -1) queue.splice(index, 1);
          reject(abortError(signal));
        };
        queue.push(entry);
        signal?.addEventListener('abort', onAbort, { once: true });
      });
    },
  };
}
